import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PostCard } from "@/components/PostCard";
import { Input } from "@/components/ui/input";
import {
  Users, TrendingUp, Heart, MessageCircle, FileText,
  Coins, Send, Music, BarChart3, ChevronRight, MapPin, Zap, Radio, Gift, Plus, Trash2, ToggleLeft, ToggleRight, Trophy, CheckCircle, XCircle, ImagePlus, Edit3,
  DollarSign, Rocket, Calendar, Ticket,
} from "lucide-react";
import { PollCard } from "@/components/PollCard";
import { CreatePollForm } from "@/components/CreatePollForm";
import { DashboardRewardsTab } from "@/components/dashboard/DashboardRewardsTab";
import { DashboardChallengesTab } from "@/components/dashboard/DashboardChallengesTab";
import { DashboardRevenueTab } from "@/components/dashboard/DashboardRevenueTab";
import { DashboardFansTab } from "@/components/dashboard/DashboardFansTab";
import { WeeklyChallengeCard } from "@/components/dashboard/WeeklyChallengeCard";
import { NewArtistsBanner } from "@/components/NewArtistsBanner";
import { toast } from "sonner";

export default function ArtistDashboardPage() {
  const { api, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [posts, setPosts] = useState([]);
  const [fans, setFans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("posts");

  // Sync tab with ?tab= query param + auto-open create form + scroll
  useEffect(() => {
    const t = searchParams.get("tab");
    const validTabs = ["posts", "rewards", "challenges", "polls", "stats", "fans", "revenue"];
    if (t && validTabs.includes(t)) setTab(t);
    const create = searchParams.get("create");
    if (create === "reward") {
      setShowRewardForm(true);
      setTimeout(() => {
        document.querySelector('[data-testid="create-reward-btn"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
    if (create === "challenge") {
      setShowChallengeForm(true);
      setTimeout(() => {
        document.querySelector('[data-testid="create-challenge-btn"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
    }
  }, [searchParams]);
  const [newPost, setNewPost] = useState({ content: "", embed_url: "" });
  const [postLoading, setPostLoading] = useState(false);
  const [postImages, setPostImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [newStory, setNewStory] = useState("");
  const [storyLoading, setStoryLoading] = useState(false);
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [newLive, setNewLive] = useState({ title: "", embed_url: "", platform: "youtube" });
  const [liveLoading, setLiveLoading] = useState(false);
  const [showPollForm, setShowPollForm] = useState(false);
  const [polls, setPolls] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [showRewardForm, setShowRewardForm] = useState(false);
  const [newReward, setNewReward] = useState({ title: "", description: "", type: "custom", starting_price: 10, duration_hours: 24 });
  const [rewardLoading, setRewardLoading] = useState(false);
  const [rewardImage, setRewardImage] = useState(null);
  const [rewardImagePreview, setRewardImagePreview] = useState(null);
  const [editingReward, setEditingReward] = useState(null);
  const [editRewardImage, setEditRewardImage] = useState(null);
  const [editRewardImagePreview, setEditRewardImagePreview] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [showChallengeForm, setShowChallengeForm] = useState(false);
  const [newChallenge, setNewChallenge] = useState({ title: "", description: "", challenge_type: "share", reward_credits: 50, max_completions: -1 });
  const [challengeLoading, setChallengeLoading] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [myEvents, setMyEvents] = useState([]);
  const [boostLoading, setBoostLoading] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, postsRes, fansRes, pollsRes, rewardsRes, challengesRes, revenueRes, eventsRes] = await Promise.all([
        api.get("/me/artist/stats"),
        api.get("/me/artist/posts", { params: { limit: 20 } }),
        api.get("/me/artist/fans", { params: { limit: 30 } }),
        api.get("/polls", { params: { scope: "all", limit: 20 } }),
        api.get("/rewards/my-rewards"),
        api.get("/challenges/my-challenges"),
        api.get("/me/artist/revenue").catch(() => ({ data: null })),
        api.get("/events", { params: { upcoming: true } }).catch(() => ({ data: { events: [] } })),
      ]);
      setStats(statsRes.data);
      setPosts(postsRes.data.posts);
      setFans(fansRes.data.fans);
      setPolls(pollsRes.data.polls.filter(p => p.creator_id === user?.id));
      setRewards(rewardsRes.data.rewards);
      setChallenges(challengesRes.data.challenges);
      if (revenueRes.data) setRevenue(revenueRes.data);
      // Filter to only show artist's own events
      setMyEvents((eventsRes.data.events || []).filter(e => e.creator_user_id === user?.id));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.content.trim() && !newPost.embed_url.trim() && postImages.length === 0) return;
    setPostLoading(true);
    try {
      // Upload images first
      let mediaUrls = [];
      if (postImages.length > 0) {
        setUploadingImages(true);
        for (const img of postImages) {
          const formData = new FormData();
          formData.append("file", img);
          const upRes = await api.post("/uploads", formData, { headers: { "Content-Type": "multipart/form-data" } });
          mediaUrls.push(upRes.data.url);
        }
        setUploadingImages(false);
      }
      const type = newPost.embed_url ? "video" : mediaUrls.length > 0 ? "image" : "text";
      const res = await api.post("/posts", {
        content: newPost.content,
        embed_url: newPost.embed_url || null,
        media_urls: mediaUrls,
        type,
      });
      setPosts((prev) => [res.data.post, ...prev]);
      setNewPost({ content: "", embed_url: "" });
      setPostImages([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setPostLoading(false);
      setUploadingImages(false);
    }
  };

  const handleLike = async (postId) => {
    const res = await api.post(`/posts/${postId}/like`);
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, is_liked: res.data.liked, likes_count: res.data.likes_count }
          : p
      )
    );
  };

  const handleReact = async (postId, reactionType) => {
    const res = await api.post(`/posts/${postId}/react`, { reaction_type: reactionType });
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, user_reaction: res.data.reaction_type, reactions: res.data.reactions }
          : p
      )
    );
    return res.data;
  };

  const handleCreateStory = async () => {
    if (!newStory.trim()) return;
    setStoryLoading(true);
    try {
      await api.post("/stories", { content: newStory });
      setNewStory("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setStoryLoading(false);
  };

  const handleStartLive = async () => {
    if (!newLive.title.trim() || !newLive.embed_url.trim()) return;
    setLiveLoading(true);
    try {
      const res = await api.post("/lives", newLive);
      setShowLiveModal(false);
      setNewLive({ title: "", embed_url: "", platform: "youtube" });
      navigate(`/live/${res.data.live.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setLiveLoading(false);
  };

  const handleCreatePoll = async (data) => {
    const res = await api.post("/polls", data);
    setPolls((prev) => [res.data.poll, ...prev]);
    setShowPollForm(false);
  };

  const handleVotePoll = async (pollId, optionId) => {
    const res = await api.post(`/polls/${pollId}/vote`, { option_id: optionId });
    setPolls((prev) => prev.map((p) => (p.id === pollId ? res.data.poll : p)));
    return res.data.poll;
  };

  const handleDeletePoll = async (pollId) => {
    await api.delete(`/polls/${pollId}`);
    setPolls((prev) => prev.filter((p) => p.id !== pollId));
  };

  const handleRewardImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRewardImage(file);
    setRewardImagePreview(URL.createObjectURL(file));
  };

  const openEditReward = (reward) => {
    setEditingReward({ ...reward });
    setEditRewardImage(null);
    setEditRewardImagePreview(reward.image_url || null);
  };

  const handleEditRewardImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditRewardImage(file);
    setEditRewardImagePreview(URL.createObjectURL(file));
  };

  const handleSaveEditReward = async () => {
    if (!editingReward || !editingReward.title.trim()) return;
    setRewardLoading(true);
    try {
      const hasBids = (editingReward.total_bids || 0) > 0;
      let payload = { title: editingReward.title, description: editingReward.description };

      if (!hasBids) {
        let image_url = editingReward.image_url;
        if (editRewardImage) {
          const formData = new FormData();
          formData.append("file", editRewardImage);
          const uploadRes = await api.post("/uploads", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          image_url = uploadRes.data.url;
        }
        payload = { ...payload, type: editingReward.type, starting_price: editingReward.starting_price, image_url };
      }

      const res = await api.put(`/rewards/${editingReward.id}`, payload);
      setRewards((prev) => prev.map((r) => (r.id === editingReward.id ? res.data.reward : r)));
      setEditingReward(null);
      setEditRewardImage(null);
      setEditRewardImagePreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la modification");
    }
    setRewardLoading(false);
  };

  const handleCreateReward = async () => {
    if (!newReward.title.trim() || newReward.starting_price < 1) return;
    setRewardLoading(true);
    try {
      let image_url = null;
      if (rewardImage) {
        const formData = new FormData();
        formData.append("file", rewardImage);
        const uploadRes = await api.post("/uploads", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        image_url = uploadRes.data.url;
      }
      const res = await api.post("/rewards", { ...newReward, image_url });
      setRewards((prev) => [res.data.reward, ...prev]);
      setNewReward({ title: "", description: "", type: "custom", starting_price: 10, duration_hours: 24 });
      setRewardImage(null);
      setRewardImagePreview(null);
      setShowRewardForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setRewardLoading(false);
  };

  const handleToggleReward = async (rewardId, active) => {
    try {
      const res = await api.put(`/rewards/${rewardId}`, { active: !active });
      setRewards((prev) => prev.map((r) => (r.id === rewardId ? res.data.reward : r)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (!window.confirm("Supprimer cette recompense ?")) return;
    try {
      await api.delete(`/rewards/${rewardId}`);
      setRewards((prev) => prev.filter((r) => r.id !== rewardId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleCreateChallenge = async () => {
    if (!newChallenge.title.trim()) return;
    setChallengeLoading(true);
    try {
      const res = await api.post("/challenges", newChallenge);
      setChallenges((prev) => [res.data.challenge, ...prev]);
      setNewChallenge({ title: "", description: "", challenge_type: "share", reward_credits: 50, max_completions: -1 });
      setShowChallengeForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setChallengeLoading(false);
  };

  const handleToggleChallenge = async (challengeId) => {
    try {
      const res = await api.put(`/challenges/${challengeId}`);
      setChallenges((prev) => prev.map((c) => c.id === challengeId ? { ...c, active: res.data.active } : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDeleteChallenge = async (challengeId) => {
    if (!window.confirm("Supprimer ce defi ?")) return;
    try {
      await api.delete(`/challenges/${challengeId}`);
      setChallenges((prev) => prev.filter((c) => c.id !== challengeId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleViewSubmissions = async (challengeId) => {
    if (selectedChallenge === challengeId) {
      setSelectedChallenge(null);
      setSubmissions([]);
      return;
    }
    try {
      const res = await api.get(`/challenges/${challengeId}/submissions`);
      setSubmissions(res.data.submissions);
      setSelectedChallenge(challengeId);
    } catch (err) { console.error(err); }
  };

  const handleApproveSubmission = async (challengeId, submissionId) => {
    try {
      await api.post(`/challenges/${challengeId}/submissions/${submissionId}/approve`);
      setSubmissions((prev) => prev.map((s) => s.id === submissionId ? { ...s, status: "approved" } : s));
      setChallenges((prev) => prev.map((c) => c.id === challengeId ? { ...c, completions_count: c.completions_count + 1 } : c));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleRejectSubmission = async (challengeId, submissionId) => {
    try {
      await api.post(`/challenges/${challengeId}/submissions/${submissionId}/reject`);
      setSubmissions((prev) => prev.map((s) => s.id === submissionId ? { ...s, status: "rejected" } : s));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleBoostEvent = async (eventId) => {
    if (!window.confirm("Booster cet evenement pour 50 credits (24h de visibilite prioritaire) ?")) return;
    setBoostLoading(eventId);
    try {
      const res = await api.post("/me/artist/boost-event", { event_id: eventId });
      toast.success(`Evenement booste ! ${res.data.credits_spent} credits depenses.`);
      refreshUser();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setBoostLoading(null);
  };

  const [boostPostModal, setBoostPostModal] = useState(null);
  const [boostPostLoading, setBoostPostLoading] = useState(false);

  const BOOST_TIERS = [
    { credits: 10, hours: "6h", color: "from-blue-400 to-blue-500" },
    { credits: 25, hours: "24h", color: "from-[#FF6B35] to-[#FF8FA3]" },
    { credits: 50, hours: "3j", color: "from-purple-500 to-pink-500" },
    { credits: 100, hours: "7j", color: "from-yellow-400 to-orange-500" },
  ];

  const handleBoostPost = async (postId, credits) => {
    setBoostPostLoading(true);
    try {
      await api.post(`/posts/${postId}/boost`, { credits });
      setBoostPostModal(null);
      refreshUser();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setBoostPostLoading(false);
  };

  if (loading) {
    return (
      <div className="py-6 space-y-4">
        <div className="bg-foreground/5 rounded-3xl h-40 animate-pulse" />
        <div className="bg-foreground/5 rounded-3xl h-24 animate-pulse" />
      </div>
    );
  }

  const artist = stats?.artist;
  const s = stats?.stats;

  if (!artist) {
    return (
      <div className="py-6 text-center">
        <Music className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Profil artiste non trouve</p>
      </div>
    );
  }

  const scoreColor = artist.current_score >= 1.5
    ? "text-emerald-400"
    : artist.current_score >= 1.0
    ? "text-[#FF6B35]"
    : "text-red-400";

  return (
    <div className="py-6 space-y-6" data-testid="artist-dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">Mon Espace</h1>
          <p className="text-sm text-muted-foreground">Bienvenue, {artist.name}</p>
        </div>
        <button
          onClick={() => navigate(`/artist/${artist.id}`)}
          className="flex items-center gap-1 text-xs text-[#FF6B35] hover:text-[#FF8FA3] transition-colors"
          data-testid="view-public-profile"
        >
          Voir profil public
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* Bandeau Nouveaux Artistes - encourager les artistes a se soutenir entre eux */}
      <NewArtistsBanner />

      {/* Defi hebdo */}
      <WeeklyChallengeCard />

      {/* Quick Actions - "Que veux-tu faire aujourd'hui ?" */}
      <div className="bg-card border border-border rounded-3xl p-4 shadow-sm" data-testid="quick-actions-section">
        <h3 className="font-heading font-bold text-sm text-card-foreground mb-3">Que veux-tu faire aujourd'hui ?</h3>
        <div className="grid grid-cols-2 gap-2.5">
          {[
            { id: "post", label: "Publier", desc: "Post & medias", icon: Send, gradient: "from-[#FF6B35] to-[#FF8FA3]", action: () => { setTab("posts"); document.querySelector('[data-testid="post-content-input"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); } },
            { id: "auction", label: "Lancer une enchere", desc: "Goodies, dedicaces", icon: Gift, gradient: "from-[#7000FF] to-[#FF6B35]", action: () => { setTab("rewards"); setShowRewardForm(true); setTimeout(() => document.querySelector('[data-testid="create-reward-btn"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 300); } },
            { id: "challenge", label: "Creer un defi", desc: "Engager les fans", icon: Trophy, gradient: "from-emerald-500 to-teal-400", action: () => { setTab("challenges"); setShowChallengeForm(true); setTimeout(() => document.querySelector('[data-testid="create-challenge-btn"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 300); } },
            { id: "ticket", label: "Billetterie", desc: "Concerts, lives", icon: Ticket, gradient: "from-[#FF8FA3] to-[#7000FF]", action: () => navigate("/events?create=1") },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={action.action}
                className="bg-white border border-border rounded-2xl p-3 flex items-start gap-2.5 hover:border-[#FF6B35]/30 active:scale-95 transition-all group text-left"
                data-testid={`quick-action-${action.id}`}
              >
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${action.gradient} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-xs text-card-foreground truncate">{action.label}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{action.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-3" data-testid="artist-stats">
        <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-sm">
          <Users className="w-5 h-5 text-[#FF8FA3] mx-auto mb-1.5" />
          <p className="font-mono-credits font-bold text-xl text-card-foreground">{s?.fan_count || 0}</p>
          <p className="text-[10px] text-muted-foreground">Fans</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-sm">
          <TrendingUp className={`w-5 h-5 mx-auto mb-1.5 ${scoreColor}`} />
          <p className={`font-mono-credits font-bold text-xl ${scoreColor}`}>
            {artist.current_score?.toFixed(2)}
          </p>
          <p className="text-[10px] text-muted-foreground">Cote</p>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-sm">
          <Coins className="w-5 h-5 text-[#FF6B35] mx-auto mb-1.5" />
          <p className="font-mono-credits font-bold text-xl text-card-foreground">{s?.total_bets || 0}</p>
          <p className="text-[10px] text-muted-foreground">Mises</p>
        </div>
      </div>

      {/* Engagement row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 shadow-sm">
          <FileText className="w-4 h-4 text-muted-foreground" />
          <div>
            <p className="font-mono-credits font-bold text-sm text-card-foreground">{s?.total_posts || 0}</p>
            <p className="text-[10px] text-muted-foreground">Posts</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 shadow-sm">
          <Heart className="w-4 h-4 text-[#FF8FA3]" />
          <div>
            <p className="font-mono-credits font-bold text-sm text-card-foreground">{s?.total_likes || 0}</p>
            <p className="text-[10px] text-muted-foreground">Likes</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl p-3 flex items-center gap-2 shadow-sm">
          <MessageCircle className="w-4 h-4 text-blue-400" />
          <div>
            <p className="font-mono-credits font-bold text-sm text-card-foreground">{s?.total_comments || 0}</p>
            <p className="text-[10px] text-muted-foreground">Comm.</p>
          </div>
        </div>
      </div>

      {/* Live + Poll buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => setShowLiveModal(true)}
          className="flex-1 bg-card border border-red-200 rounded-2xl p-4 flex items-center gap-3 hover:border-red-400/40 transition-colors shadow-sm"
          data-testid="dashboard-start-live-btn"
        >
          <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center">
            <Radio className="w-5 h-5 text-red-500" />
          </div>
          <div className="text-left">
            <p className="font-heading font-bold text-sm text-card-foreground">Lancer un Live</p>
            <p className="text-[10px] text-muted-foreground">YouTube, TikTok, Twitch...</p>
          </div>
        </button>
        <button
          onClick={() => setShowPollForm(!showPollForm)}
          className="flex-1 bg-card border border-[#7000FF]/20 rounded-2xl p-4 flex items-center gap-3 hover:border-[#7000FF]/40 transition-colors shadow-sm"
          data-testid="dashboard-create-poll-btn"
        >
          <div className="w-10 h-10 rounded-full bg-[#7000FF]/15 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-[#7000FF]" />
          </div>
          <div className="text-left">
            <p className="font-heading font-bold text-sm text-card-foreground">Sondage</p>
            <p className="text-[10px] text-muted-foreground">Engage ta communaute</p>
          </div>
        </button>
      </div>

      {/* Event & Ticketing quick action */}
      <button
        onClick={() => navigate("/events")}
        className="w-full bg-gradient-to-r from-[#FF6B35]/10 to-[#7000FF]/10 border border-[#FF6B35]/30 rounded-2xl p-5 flex items-center gap-4 hover:border-[#FF6B35]/60 transition-colors shadow-sm"
        data-testid="dashboard-events-btn"
      >
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
          <Ticket className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1 text-left">
          <p className="font-heading font-bold text-base text-card-foreground">Evenements & Billetterie</p>
          <p className="text-xs text-muted-foreground">Creer un concert, gerer tes billets et suivre les ventes</p>
        </div>
        <ChevronRight className="w-5 h-5 text-[#FF6B35]" />
      </button>

      {/* Boost my posts section */}
      {posts.length > 0 && (
        <div className="bg-card border border-border rounded-3xl p-5 space-y-3 shadow-sm" data-testid="boost-posts-section">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-sm text-card-foreground">Mettre en avant mes publications</h3>
              <p className="text-[10px] text-muted-foreground">Depense des credits pour que tes posts apparaissent en priorite dans le feed</p>
            </div>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {posts.slice(0, 5).map((p) => (
              <div key={p.id} className={`flex items-center gap-3 p-3 rounded-2xl border transition-all ${p.is_boosted ? "border-[#FF6B35]/40 bg-[#FF6B35]/5" : "border-border hover:border-[#FF6B35]/30"}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-card-foreground font-medium truncate">{p.content?.substring(0, 60) || "Publication"}{p.content?.length > 60 ? "..." : ""}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-muted-foreground">{p.likes_count || 0} likes</span>
                    <span className="text-[10px] text-muted-foreground">{p.comments_count || 0} com.</span>
                  </div>
                </div>
                {p.is_boosted ? (
                  <span className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 text-[10px] font-bold text-[#FF6B35]">
                    <Rocket className="w-3 h-3" /> En avant
                  </span>
                ) : (
                  <button
                    onClick={() => setBoostPostModal(p)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-bold hover:scale-105 active:scale-95 transition-transform shadow-sm"
                    data-testid={`dashboard-boost-${p.id}`}
                  >
                    <Rocket className="w-3.5 h-3.5" /> Booster
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-center text-muted-foreground">Ton solde : <span className="font-mono-credits font-bold text-[#FF6B35]">{user?.credits_balance || 0}</span> credits</p>
        </div>
      )}

      {/* Boost Post Modal */}
      {boostPostModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setBoostPostModal(null)} data-testid="dashboard-boost-modal">
          <div className="bg-card rounded-3xl p-5 w-80 max-w-[90vw] shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
                  <Rocket className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <h3 className="font-heading font-bold text-sm text-card-foreground">Booster cette publication</h3>
              </div>
              <button onClick={() => setBoostPostModal(null)} className="text-muted-foreground hover:text-card-foreground"><XCircle className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-1 truncate">"{boostPostModal.content?.substring(0, 50)}..."</p>
            <p className="text-xs text-muted-foreground mb-4">Apparaitra en priorite dans le feed de tous les utilisateurs.</p>
            <div className="space-y-2">
              {BOOST_TIERS.map((opt) => (
                <button
                  key={opt.credits}
                  onClick={() => handleBoostPost(boostPostModal.id, opt.credits)}
                  disabled={boostPostLoading}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl border border-border hover:border-[#FF6B35]/40 hover:scale-[1.01] active:scale-[0.99] transition-all group"
                  data-testid={`dashboard-boost-opt-${opt.credits}`}
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${opt.color} flex items-center justify-center shrink-0`}>
                    <Rocket className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-bold text-sm text-card-foreground group-hover:text-[#FF6B35] transition-colors">{opt.hours} de mise en avant</p>
                    <p className="text-[10px] text-muted-foreground">Priorite dans le feed</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono-credits font-bold text-sm text-[#FF6B35]">{opt.credits}</p>
                    <p className="text-[9px] text-muted-foreground">credits</p>
                  </div>
                </button>
              ))}
            </div>
            <p className="text-[10px] text-center text-muted-foreground mt-3">Ton solde : <span className="font-mono-credits font-bold text-[#FF6B35]">{user?.credits_balance || 0}</span> credits</p>
          </div>
        </div>
      )}

      {/* My Events summary */}
      {myEvents.length > 0 && (
        <div className="bg-card border border-border rounded-3xl p-5 space-y-3 shadow-sm" data-testid="my-events-summary">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-sm text-card-foreground flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-[#FF6B35]" />
              Mes evenements ({myEvents.length})
            </h3>
            <button onClick={() => navigate("/events")} className="text-[10px] text-[#FF6B35] font-bold">
              Voir tout
            </button>
          </div>
          <div className="space-y-2">
            {myEvents.slice(0, 3).map((e) => (
              <div key={e.id} className="bg-white border border-border rounded-xl p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-xs text-card-foreground truncate">{e.title}</p>
                  <p className="text-[10px] text-muted-foreground">{e.date}{e.location ? ` - ${e.location}` : ""}</p>
                </div>
                <button
                  onClick={() => navigate(`/events/${e.id}/tickets`)}
                  className="flex items-center gap-1 bg-[#FF6B35] text-white font-bold px-3 py-1.5 rounded-full text-[10px] hover:scale-105 active:scale-95 transition-transform shrink-0"
                  data-testid={`manage-tickets-${e.id}`}
                >
                  <Ticket className="w-3 h-3" />
                  Billets
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Poll form */}
      {showPollForm && (
        <CreatePollForm onSubmit={handleCreatePoll} onCancel={() => setShowPollForm(false)} />
      )}

      {/* Story creation */}
      <div className="bg-card border border-border rounded-3xl p-5 space-y-3 shadow-sm" data-testid="dashboard-create-story">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#FF8FA3]" />
          <h3 className="font-heading font-bold text-sm text-card-foreground">Story rapide</h3>
          <span className="text-[10px] text-muted-foreground">Disparait dans 24h</span>
        </div>
        <div className="flex gap-2">
          <input
            placeholder="Quoi de neuf ?"
            value={newStory}
            onChange={(e) => setNewStory(e.target.value)}
            maxLength={280}
            className="flex-1 bg-white border border-border rounded-full px-4 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
            data-testid="dashboard-story-input"
          />
          <button
            onClick={handleCreateStory}
            disabled={storyLoading || !newStory.trim()}
            className="bg-[#FF8FA3] text-white font-bold px-4 py-2 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="dashboard-story-submit"
          >
            {storyLoading ? "..." : "Story"}
          </button>
        </div>
      </div>

      {/* Post creation */}
      <form
        onSubmit={handleCreatePost}
        className="bg-card border border-border rounded-3xl p-5 space-y-3 shadow-sm"
        data-testid="dashboard-create-post"
      >
        <h3 className="font-heading font-bold text-sm text-card-foreground">Publier</h3>
        <textarea
          placeholder="Partage quelque chose avec tes fans..."
          value={newPost.content}
          onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
          className="w-full bg-white border border-border rounded-xl px-4 py-3 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-24"
          data-testid="dashboard-post-content"
        />
        <Input
          placeholder="Lien YouTube, Spotify, SoundCloud, Deezer, Apple Music, Tidal... (optionnel)"
          value={newPost.embed_url}
          onChange={(e) => setNewPost({ ...newPost, embed_url: e.target.value })}
          className="bg-white border-border rounded-xl text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-10 text-sm"
          data-testid="dashboard-post-embed"
        />
        {/* Image upload */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground hover:text-[#FF6B35] transition-colors">
            <ImagePlus className="w-4 h-4" />
            <span>Ajouter des images</span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setPostImages((prev) => [...prev, ...files].slice(0, 4));
                e.target.value = "";
              }}
              data-testid="dashboard-post-images"
            />
          </label>
          {postImages.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {postImages.map((img, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-border">
                  <img src={URL.createObjectURL(img)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setPostImages((prev) => prev.filter((_, j) => j !== i))} className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-[8px] leading-none">&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={postLoading || (!newPost.content.trim() && !newPost.embed_url.trim() && postImages.length === 0)}
          className="bg-[#FF6B35] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform duration-200 disabled:opacity-50 flex items-center gap-2"
          data-testid="dashboard-post-submit"
        >
          <Send className="w-4 h-4" />
          {uploadingImages ? "Upload images..." : postLoading ? "..." : "Publier"}
        </button>
      </form>

      {/* Tabs: Posts / Fans / Rewards */}
      <div className="flex gap-1 bg-muted rounded-full p-1 border border-border">
        <button
          onClick={() => setTab("posts")}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
            tab === "posts" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="dashboard-tab-posts"
        >
          <FileText className="w-3.5 h-3.5" />
          Posts ({posts.length})
        </button>
        <button
          onClick={() => setTab("fans")}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
            tab === "fans" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="dashboard-tab-fans"
        >
          <Users className="w-3.5 h-3.5" />
          Fans ({fans.length})
        </button>
        <button
          onClick={() => setTab("rewards")}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
            tab === "rewards" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="dashboard-tab-rewards"
        >
          <Gift className="w-3.5 h-3.5" />
          Encheres ({rewards.length})
        </button>
        <button
          onClick={() => setTab("challenges")}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
            tab === "challenges" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="dashboard-tab-challenges"
        >
          <Trophy className="w-3.5 h-3.5" />
          Defis
        </button>
        <button
          onClick={() => setTab("revenue")}
          className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-full text-xs font-medium transition-all duration-200 ${
            tab === "revenue" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="dashboard-tab-revenue"
        >
          <DollarSign className="w-3.5 h-3.5" />
          Revenus
        </button>
      </div>

      {/* Content */}
      {tab === "posts" ? (
        <div className="space-y-4">
          {/* Polls */}
          {polls.length > 0 && (
            <div className="space-y-4">
              {polls.map((p) => (
                <PollCard key={p.id} poll={p} onVote={handleVotePoll} onDelete={handleDeletePoll} />
              ))}
            </div>
          )}
          {posts.length === 0 && polls.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Tu n'as pas encore publie</p>
              <p className="text-xs text-muted-foreground mt-1">Utilise le formulaire ci-dessus pour partager du contenu</p>
            </div>
          ) : (
            posts.map((p) => <PostCard key={p.id} post={p} onLike={handleLike} onReact={handleReact} onDelete={(id) => setPosts((prev) => prev.filter((x) => x.id !== id))} onUpdate={(id, updated) => setPosts((prev) => prev.map((x) => x.id === id ? { ...x, ...updated } : x))} />)
          )}
        </div>
      ) : tab === "rewards" ? (
        <DashboardRewardsTab
          rewards={rewards} showRewardForm={showRewardForm} setShowRewardForm={setShowRewardForm}
          newReward={newReward} setNewReward={setNewReward} rewardLoading={rewardLoading}
          rewardImage={rewardImage} rewardImagePreview={rewardImagePreview}
          handleRewardImageChange={handleRewardImageChange} handleCreateReward={handleCreateReward}
          handleDeleteReward={handleDeleteReward} openEditReward={openEditReward}
          editingReward={editingReward} setEditingReward={setEditingReward}
          editRewardImagePreview={editRewardImagePreview}
          handleEditRewardImageChange={handleEditRewardImageChange}
          handleSaveEditReward={handleSaveEditReward}
          setEditRewardImage={setEditRewardImage} setEditRewardImagePreview={setEditRewardImagePreview}
        />
      ) : tab === "challenges" ? (
        <DashboardChallengesTab
          challenges={challenges} showChallengeForm={showChallengeForm}
          setShowChallengeForm={setShowChallengeForm} newChallenge={newChallenge}
          setNewChallenge={setNewChallenge} challengeLoading={challengeLoading}
          handleCreateChallenge={handleCreateChallenge} handleToggleChallenge={handleToggleChallenge}
          handleDeleteChallenge={handleDeleteChallenge} handleViewSubmissions={handleViewSubmissions}
          selectedChallenge={selectedChallenge} submissions={submissions}
          handleApproveSubmission={handleApproveSubmission} handleRejectSubmission={handleRejectSubmission}
        />
      ) : tab === "revenue" ? (
        <DashboardRevenueTab revenue={revenue} myEvents={myEvents} handleBoostEvent={handleBoostEvent} boostLoading={boostLoading} />
      ) : (
        <DashboardFansTab fans={fans} />
      )}

      {/* Live modal */}
      {/* Live modal */}
      {showLiveModal && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="live-modal">
          <div className="bg-card border border-border rounded-3xl p-6 w-full max-w-md space-y-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-card-foreground">Lancer un Live</h3>
              <button onClick={() => setShowLiveModal(false)} className="text-muted-foreground hover:text-card-foreground">
                <span className="text-xl">&times;</span>
              </button>
            </div>
            <input
              placeholder="Titre du live"
              value={newLive.title}
              onChange={(e) => setNewLive({ ...newLive, title: e.target.value })}
              className="w-full bg-white border border-border rounded-xl px-4 py-3 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
              data-testid="live-title-input"
            />
            <input
              placeholder="Lien du live (YouTube, TikTok, Twitch...)"
              value={newLive.embed_url}
              onChange={(e) => setNewLive({ ...newLive, embed_url: e.target.value })}
              className="w-full bg-white border border-border rounded-xl px-4 py-3 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none"
              data-testid="live-url-input"
            />
            <div>
              <p className="text-xs text-muted-foreground mb-2">Plateforme</p>
              <div className="flex flex-wrap gap-1.5">
                {["youtube", "tiktok", "facebook", "twitch"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setNewLive({ ...newLive, platform: p })}
                    className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-all ${
                      newLive.platform === p
                        ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                        : "bg-white border-border text-muted-foreground"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={handleStartLive}
              disabled={liveLoading || !newLive.title.trim() || !newLive.embed_url.trim()}
              className="w-full bg-red-500 text-white font-bold py-3 rounded-full text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="live-start-submit"
            >
              <Radio className="w-4 h-4" />
              {liveLoading ? "..." : "Passer en live"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
